/**
 * Seattle Metro Solo - Phase 9: 商品補充 Move
 *
 * @see docs/specifications/logic/seattle_metro_solo/phase_09_goods_growth_seattle.md
 */

import { INVALID_MOVE } from 'boardgame.io/core';
import { debug } from '../../utils/debug';
import type { Ctx } from 'boardgame.io';
import type { SeattleGameState, SeattleGoodsPlacementInfo } from '../../types';
import {
  validatePlaceGoodsSeattle,
  PlaceGoodsSeattleArgs,
  getDiceValueForLocation,
} from '../validators/goodsGrowthSeattleValidator';

/**
 * 商品配置 Move（Seattle固有）
 *
 * @param G - Seattle ゲーム状態
 * @param ctx - Boardgame.io コンテキスト
 * @param args - 商品配置引数
 * @returns void or INVALID_MOVE
 */
export function placeGoodsSeattle(
  { G, ctx }: { G: SeattleGameState; ctx: Ctx },
  args: PlaceGoodsSeattleArgs
): void | typeof INVALID_MOVE {
  // バリデーション
  const validation = validatePlaceGoodsSeattle(G, ctx, args);
  if (!validation.success) {
    console.error(`[placeGoodsSeattle] Validation failed: ${validation.error} - ${validation.message}`);
    return INVALID_MOVE;
  }

  const { cubeIndex, locationId } = args;
  const { remainingDice, remainingCubes } = G.pendingGoodsPlacement!;

  // 1. キューブの色を取得
  const color = remainingCubes[cubeIndex];

  // 2. ロケーションに対応するダイス目を取得
  const diceValue = getDiceValueForLocation(locationId)!;

  // 3. locationを探す
  const hex = G.map.find(h => h.city?.id === locationId || h.town?.id === locationId);
  if (!hex) {
    console.error(`[placeGoodsSeattle] Location not found: ${locationId}`);
    return INVALID_MOVE;
  }

  // 4. 商品を配置
  if (!hex.goods) {
    hex.goods = [];
  }
  hex.goods.push(color);

  // 5. 配置情報を記録
  const placementInfo: SeattleGoodsPlacementInfo = {
    diceValue,
    color,
    locationName: hex.city?.name || hex.town?.id || locationId,
    locationId,
    locationType: hex.city ? 'city' : 'town',
  };

  if (!G.lastGoodsPlacement) {
    G.lastGoodsPlacement = [];
  }
  G.lastGoodsPlacement.push(placementInfo);

  // 6. pendingGoodsPlacement を更新（ダイスとキューブを消費）
  // ダイス目を1つ消費（同じ値が複数ある場合は最初の1つ）
  const diceIndex = remainingDice.indexOf(diceValue);
  remainingDice.splice(diceIndex, 1);
  // キューブを消費
  remainingCubes.splice(cubeIndex, 1);

  debug.log(`[placeGoodsSeattle] Placed ${color} at ${locationId} (dice: ${diceValue}). Remaining: dice=${remainingDice.join(',')}, cubes=${remainingCubes.join(',')}`);
}

/**
 * 需要過多によるゲームオーバーをトリガー
 *
 * @param G - Seattle ゲーム状態
 * @param ctx - Boardgame.io コンテキスト
 */
export function triggerDemandOverload(
  { G }: { G: SeattleGameState; ctx: Ctx }
): void {
  G.isGameOver = true;
  G.endReason = 'DEFEAT_DEMAND_OVERLOAD';
  G.autoPhaseResult = {
    phase: 'refillGoods',
    message: 'No available location! Demand exceeded capacity.',
    floatingText: 'Demand Overload!',
    details: { demandOverload: true, location: 'ALL_FULL' }
  };
  debug.log(`[triggerDemandOverload] Demand overload - DEFEAT`);
}

/**
 * 商品配置をリセット（Cancel 機能）
 *
 * Airport 自動配置とダイスロール後の状態に戻す:
 * - 配置した商品をヘクスから削除
 * - pendingGoodsPlacement のダイスとキューブを復元
 * - lastGoodsPlacement をクリア
 */
export function resetGoodsPlacement(
  { G }: { G: SeattleGameState; ctx: Ctx }
): void {
  // 1. lastGoodsPlacement に記録された配置を元に戻す
  if (G.lastGoodsPlacement && G.lastGoodsPlacement.length > 0) {
    for (const placement of G.lastGoodsPlacement) {
      const hex = G.map.find(h => h.city?.id === placement.locationId || h.town?.id === placement.locationId);
      if (hex && hex.goods) {
        // 該当色の商品を1つ削除
        const colorIndex = hex.goods.indexOf(placement.color);
        if (colorIndex !== -1) {
          hex.goods.splice(colorIndex, 1);
          debug.log(`[resetGoodsPlacement] Removed ${placement.color} from ${placement.locationId}`);
        }
      }

      // 2. pendingGoodsPlacement のダイスとキューブを復元
      if (G.pendingGoodsPlacement) {
        G.pendingGoodsPlacement.remainingDice.push(placement.diceValue);
        G.pendingGoodsPlacement.remainingCubes.push(placement.color);
      }
    }
    G.lastGoodsPlacement = [];
  }

  debug.log(`[resetGoodsPlacement] Goods placement reset to initial state`);
}
